/**
 * Feb-2026 (User-Wunsch) — Provisionsabrechnung (Vermittlungsprovision).
 *
 * Workflow-Schritt zwischen „Übergabe & TÜV" und „Papiere versenden".
 * Erfasst Empfänger, Betrag (Default = Vermittlungsprovision des Inserats),
 * Datum, Status (offen / abgerechnet / ausgezahlt) und einen Vermerk.
 */
import { useState } from "react";
import { Percent, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useAutosaveOnBlur, AutosaveBadge } from "./useAutosaveOnBlur";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import FrequentSelect from "../FrequentSelect";
import api from "../../../lib/api";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";
import SellerCommissionBlock from "./SellerCommissionBlock";

const STATUS = [
    { value: "offen", label: "Offen" },
    { value: "abgerechnet", label: "Abgerechnet" },
    { value: "ausgezahlt", label: "Ausgezahlt" },
];
const todayISO = () => new Date().toISOString().slice(0, 10);

export function CommissionSettlementModal({ listingId, existing, defaultAmount, onBack, onClose, onSaved }) {
    const [form, setForm] = useState({
        recipient: existing?.recipient || "",
        amount: existing?.amount != null ? existing.amount : (defaultAmount || ""),
        date: existing?.date || todayISO(),
        status: existing?.status || "offen",
        note: existing?.note || "",
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    // Jun-2026 (User-Wunsch) — Auto-Speichern beim Verlassen eines Feldes.
    const autosave = useAutosaveOnBlur(async () => {
        if (!(form.recipient || parseFloat(form.amount))) return false;
        try {
            const r = await api.post(`/listings/${listingId}/dispo/commission-settlement`, { ...form, amount: parseFloat(form.amount) || 0 });
            onSaved?.(r.data?.commission);
            return true;
        } catch { return false; }
    }, () => JSON.stringify(form));

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/commission-settlement`, {
                ...form,
                amount: parseFloat(form.amount) || 0,
            });
            toast.success("Provisionsabrechnung gespeichert");
            onSaved?.(r.data?.commission);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="w-[96vw] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dispo-modal-commission" onBlur={autosave.onBlur}>
                <FeatureGuideTriangle guideKey="commission" />
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Percent className="w-5 h-5 text-indigo-600" /> Provisionsabrechnung
                    </DialogTitle>
                </DialogHeader>
                <AutosaveBadge status={autosave.status} savedAt={autosave.savedAt} />

                {/* Jul-2026 (User-Wunsch) — Verkäufer-Provision + Vermittler-Auszahlung an EINEM Ort. */}
                <SellerCommissionBlock listingId={listingId} />

                <div className="border-t border-gray-200 my-3" />
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5" data-testid="commission-payout-heading">
                    <Percent className="w-3.5 h-3.5" /> Auszahlung / Vermittler-Provision
                </div>

                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Empfänger (Vermittler)</Label>
                        <Input value={form.recipient} onChange={(e) => set("recipient", e.target.value)} placeholder="Name des Vermittlers" className="mt-1 rounded-sm" data-testid="commission-recipient" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Betrag €</Label>
                            <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0,00" className="mt-1 rounded-sm font-mono-num" data-testid="commission-amount" />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Datum</Label>
                            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1 rounded-sm font-mono-num" data-testid="commission-date" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Status</Label>
                        <FrequentSelect
                            storageKey="dispo.commission_status"
                            value={form.status}
                            onValueChange={(v) => set("status", v)}
                            options={STATUS}
                            triggerClassName="mt-1 rounded-sm"
                            testId="commission-status"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Vermerk</Label>
                        <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} placeholder="Freitext …" className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#111827]" data-testid="commission-note" />
                    </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-4">
                    <DispoBackButton onBack={onBack} />
                    <Button type="button" variant="outline" className="rounded-sm" onClick={() => onClose?.()} data-testid="commission-cancel">Abbrechen</Button>
                    <Button type="button" className="rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] font-bold" onClick={save} disabled={saving} data-testid="commission-save">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Speichern
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
